import { useCallback, useEffect, useRef, useState } from 'react';
import { sanitize } from './sanitize';

export function TermsModal({
  content,
  onAccept,
  onDecline,
}: {
  content: string;
  onAccept: () => void;
  onDecline: () => void;
}) {
  const dialogRef = useRef<HTMLDialogElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);
  const [hasScrolledToBottom, setHasScrolledToBottom] = useState(false);

  useEffect(() => {
    dialogRef.current?.showModal();
  }, []);

  const handleScroll = useCallback(() => {
    const el = scrollRef.current;
    if (!el) return;
    const atBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 8;
    if (atBottom) setHasScrolledToBottom(true);
  }, []);

  return (
    <dialog
      ref={dialogRef}
      onCancel={onDecline}
      className="m-auto p-0 w-full max-w-xl rounded-2xl shadow-2xl backdrop:bg-black/50"
      aria-label="Tävlingsvillkor"
    >
      <div className="bg-whitew-full flex flex-col max-h-[80dvh]">
        <div className="px-6 pt-6 pb-4 border-b border-dark-blue/10 rounded-t-2xl">
          <h2 className="text-lg font-bold text-dark-blue">Tävlingsvillkor</h2>
          <p className="text-sm text-dark-blue/60 mt-1">
            Läs igenom villkoren för att kunna godkänna.
          </p>
        </div>
        <div
          ref={scrollRef}
          onScroll={handleScroll}
          className="flex-1 overflow-y-auto overscroll-contain px-6 py-4 text-dark-blue leading-normal max-w-none [&>p]:mb-4 [&_ul]:my-4 [&_ul_li]:list-disc [&_ul]:pl-5 [&_ol]:mb-4 [&_ol_li]:list-decimal [&_ol]:pl-5 [&_li]:mb-1 [&_ol>li]:mb-3 [&_ol>li::marker]:font-bold max-h-[500px]"
        >
          <span tabIndex={0} aria-hidden="true" className="sr-only" />
          <div dangerouslySetInnerHTML={{ __html: sanitize(content) }} />
        </div>
        <div className="px-6 py-4 border-t border-dark-blue/10 flex gap-3 justify-end">
          <button
            type="button"
            onClick={onDecline}
            className="px-5 py-2.5 text-sm font-medium rounded border border-dark-blue/20 text-dark-blue hover:bg-dark-blue/5 transition-colors"
          >
            Godkänner inte
          </button>
          <button
            type="button"
            onClick={onAccept}
            disabled={!hasScrolledToBottom}
            className={[
              'px-5 py-2.5 text-sm font-bold rounded transition-colors',
              hasScrolledToBottom
                ? 'bg-dark-blue text-white hover:bg-dark-blue/80'
                : 'bg-dark-blue/20 text-dark-blue/40 cursor-not-allowed',
            ].join(' ')}
          >
            Godkänner
          </button>
        </div>
      </div>
    </dialog>
  );
}
